import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useInboxPanel } from "@/hooks/useInboxPanel";

/**
 * Copies RO side-panel state out of the URL and into the InboxPanel provider.
 *
 * `useRoPanelParams` reads `url ?? remembered`, which is enough to *render* a
 * deep link correctly -- but it would leave the provider on its previous value,
 * so the next navigation that drops the query string (the grid/list links in
 * ViewToggle) would revert to that stale value. Adoption closes that gap.
 *
 * The sync is one-way and conditional:
 *  - param non-empty and different from the provider -> adopt it;
 *  - param empty or absent -> ignore it. Absence is exactly the case the
 *    provider exists to cover, so it must never clear -- and an empty value
 *    (e.g. `?tab=`) is treated the same way rather than adopted as `""`.
 *
 * It never writes back to the URL, so it cannot loop with the setters in
 * `useRoPanelParams`. Mount it once, inside the router, on routes that own
 * these params.
 */
export const useRoPanelParamsSync = (): void => {
  const [searchParams] = useSearchParams();
  const { rememberedTab, rememberedCardId, rememberTab, rememberCardId } =
    useInboxPanel();

  const urlTab = searchParams.get("tab");
  const urlCardId = searchParams.get("ro");

  useEffect(() => {
    if (urlTab && urlTab !== rememberedTab) {
      rememberTab(urlTab);
    }
  }, [urlTab, rememberedTab, rememberTab]);

  useEffect(() => {
    if (urlCardId && urlCardId !== rememberedCardId) {
      rememberCardId(urlCardId);
    }
  }, [urlCardId, rememberedCardId, rememberCardId]);
};
